<template>
  <div>
    <BreadCrumb
      :path="[
        {
          title: documentData?.organization?.name || userData.display_name,
          route: documentData?.organization?.name
            ? `/organization/${documentData?.organization?._id}`
            : '/account',
        },
        {
          title: documentData?.title,
          route:
            documentData?.discussionType === 'curate'
              ? this.$router.routesMap.CuratorDebate(documentData?._id)
              : this.$router.routesMap.Debate(documentData?._id),
        },
        {
          title: title,
          route: `/team-settings/${documentData?._id}/${teamID}`,
        },
      ]"
    />
    <MainContentPanel
      :title="title + ': ' + documentData?.title"
      :subTitle="showSubTitle ? documentData?.title : null"
      buttonText="Add New"
      buttonIcon="Create-Team-Member"
      :buttonAction="clickMainButton"
      tooltip="Invite people to join your team."
      padding="small"
    >
      <PrimeDataTable
        :key="teamLoaded"
        ref="dataTable"
        class="uk-margin-top"
        :tableConfig="tableConfig"
        :tableOptions="tableOptions"
        :loadDataFunction="getTeamMembers"
        @selectedColumns="tableToggleData"
      />
    </MainContentPanel>
    <div uk-model ref="newMemberFormModal">
      <BasicModal
        :class="{
          newMemberFormWidth: contactView || teamView,
          newMemberFormImportView: !teamView || !contactView,
        }"
        :heading="{ title: 'Assemble Your Cohort ', icon: 'users' }"
        :showClose="true"
      >
        <StepForm
          :activeStep="currentStep"
          :stepsVisited="maxStepVisited"
          :step="1"
          :totalSteps="totalSteps"
          @stepChange="handleStep"
        >
          <template v-slot:header>
            <HeadingSecondary
              titleIcon="users"
              :small="true"
              :title="
                teamLoaded >= 1 || documentData?.discussionType === 'curate'
                  ? 'Add Team Members'
                  : 'Invite Team Member'
              "
            />
          </template>
          <template v-slot:body>
            <TeamMemberForm
              :memberData="teamMemberInvitation"
              ref="teamMemberForm"
              :teamID="teamID"
              mode="member"
              :modalView="true"
              @invalidated="teamMemberFormInvalid"
            />
          </template>
        </StepForm>
        <StepForm
          :activeStep="currentStep"
          :stepsVisited="maxStepVisited"
          :step="2"
          :totalSteps="totalSteps"
          @stepChange="handleStep"
        >
          <template v-slot:header>
            <HeadingSecondary
              titleIcon="/icons/blue/icon-blue-inlinerule.svg"
              :customIcon="true"
              :small="true"
              :title="
                teamLoaded >= 1
                  ? 'Amend General Instructions (For Future Release)'
                  : 'Give General Instructions (For Future Release)'
              "
            />
          </template>
          <template v-slot:body>
            <TipTap
              class="tip-tap-text-area"
              v-model="generalInstructions.instructions"
              placeholder="Clearly state any rules for your team..."
              :max-characters="280"
              :disabled="true"
            />
            <div class="uk-flex uk-flex-column">
              <DateTimePicker
                class="uk-margin-small-top"
                :class="{ disable: !dateTimeInputEnabled }"
                label="Replies needed by:"
                :respondTimeOptions="respondTimeOptions"
                :defaultTime="generalInstructions?.timeframes?.at(-1)?.updatedAt"
                :defaultTimeThreshold="
                  parseFloat(generalInstructions?.timeframes?.at(-1)?.timeframeThreshold)
                "
                @timeThresholdChange="
                  generalInstructions?.timeframes?.length > 0 &&
                  (generalInstructions.timeframes.at(-1).timeframeThreshold = $event)
                "
              />
              <div class="uk-flex uk-margin-bottom uk-margin-top box-margin-left">
                <label for="enable" class="uk-text-secondary">
                  <input
                    type="checkbox"
                    class="uk-checkbox"
                    disabled="true"
                    name="enable"
                    id="enable"
                    v-model="dateTimeInputEnabled"
                  />
                  {{ dateTimeInputEnabled ? 'Uncheck to disable' : 'Check to enable' }}
                </label>
              </div>
            </div>
          </template>
        </StepForm>
        <StepForm
          :activeStep="currentStep"
          :stepsVisited="maxStepVisited"
          :step="3"
          :totalSteps="totalSteps"
          @stepChange="handleStep"
        >
          <template v-slot:header>
            <HeadingSecondary
              titleIcon="/icons/black/icon-black-invitations.svg"
              :customIcon="true"
              :small="true"
              title="Custom Email Invitation"
            />
          </template>
          <template v-slot:body>
            <div>
              <span class="uk-flex">
                <span class="uk-text-bold uk-line-height-none uk-flex uk-flex-middle"
                  >S: &nbsp;</span
                >
                <input
                  type="text"
                  class="uk-input bg-white uk-margin-bottom"
                  v-model="teamMemberInvitation.subject"
              /></span>
            </div>
            <span class="uk-flex"
              ><span class="uk-text-bold">M: &nbsp;</span>
              <InvitationConfirm
                :customRow="5"
                :maxCharacters="750"
                class="uk-margin-remove message-box-width"
                @greetingMessageChange="teamMemberInvitation.greetingMessage = $event"
                :greetingMessage="teamMemberInvitation.greetingMessage"
                @customMessageChange="teamMemberInvitation.customMessage = $event"
                :customMessage="teamMemberInvitation.customMessage"
                @fixedMessageChange="teamMemberInvitation.fixedMessage = $event"
                :fixedMessage="teamMemberInvitation.fixedMessage"
            /></span>
          </template>
        </StepForm>
        <StepFormButtons
          :activeStep="currentStep"
          :totalSteps="totalSteps"
          @dataSave="handleSave"
          @stepChange="handleStep"
          :disabelBtn="disabelBtn"
          :saveButton="false"
          :finalButton="{ text: 'Send', img: 'icons/white/icon-white-invitations.svg' }"
          :finalProcessStatus="savingStatus"
          @handleSubmit="sendTeamMemberInvitation"
        />
      </BasicModal>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import HeadingSecondary from '@/components/general/HeadingSecondary';
import MainContentPanel from '@/components/display/general/MainContentPanel';
import BasicModal from '@/components/display/general/BasicModal';
import TeamMemberForm from '@/components/team/TeamMemberForm';
import BreadCrumb from '@/components/general/BreadCrumb';
import StepForm from '@/components/general/StepForm';
import StepFormButtons from '@/components/general/StepFormButtons';
import InvitationConfirm from '@/components/team/InvitationConfirm';
import DateTimePicker from '@/components/general/DateTimePicker';
import TipTap from '@/components/annotations/TipTap';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import UIkit from 'uikit';
import { xhr } from '@/xhr/endpoints';
import { EventBus } from '@/assets/js/event-bus';

export default {
  name: 'DocumentTeam',

  components: {
    HeadingSecondary,
    PrimeDataTable: defineAsyncComponent(() => import('@/components/table/PrimeDataTable.vue')),
    MainContentPanel,
    BasicModal,
    TeamMemberForm,
    BreadCrumb,
    StepForm,
    StepFormButtons,
    DateTimePicker,
    TipTap,
    InvitationConfirm,
  },

  props: {
    showSubTitle: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      title: 'Team Lineup',
      teamMemberInvitation: {
        invitees: [
          {
            firstname: '',
            email: '',
            lastname: '',
            phone: '',
            order: 1,
          },
        ],
        greetingMessage: '',
        customMessage: '',
        fixedMessage: '',
        rulesMessage: 'Clearly state any rules for your team.',
        teamName: '',
        timeThreshold: 24,
        subject: '',
      },
      respondTimeOptions: [
        {
          label: '4 Hours',
          value: 4,
        },
        {
          label: '1 Day',
          value: 24,
        },
        {
          label: '2 Days',
          value: 48,
        },
        {
          label: '3 Days',
          value: 72,
        },
        {
          label: '1 Week',
          value: 168,
        },
      ],
      currentStep: 1,
      totalSteps: 3,
      maxStepVisited: 1,
      isDataSaved: false,
      sendInvitationResult: null,
      teamLoaded: 0,
      dateTimeInputEnabled: false,
      teamView: true,
      contactView: true,
      disabelBtn: false,
      dataInvitee: {},
      teamMemberFormButtonsDef: {
        Close: {
          label: 'Close',
          action: () => UIkit.modal(this.$refs.newMemberFormModal).hide(),
        },
        Primary: {
          label: 'Next',
          icon: 'arrow-right',
          action: this.handleCreateTeamMember,
          style: 'primary',
          disabled: true,
        },
      },

      tableConfig: [
        {
          column: 'First Name',
          field: 'firstname',
          type: row => (row.user ? 'link' : undefined),
          model: row => {
            if (row.user) return row.user.firstname;
            if (row.invitation?.firstname) return row.invitation.firstname;
            return row.firstname;
          },
          link: row => (row.user ? `/team-member-details/${row.user._id}` : ''),
          sortable: true,
          index: 1,
        },
        {
          column: 'Last Name',
          field: 'lastname',
          type: row => (row.user ? 'link' : undefined),
          model: row => {
            if (row.user) return row.user.lastname;
            if (row.invitation?.lastname) return row.invitation.lastname;
            return row.lastname;
          },
          link: row => (row.user ? `/team-member-details/${row.user._id}` : ''),
          sortable: true,
          index: 2,
        },
        {
          column: 'Role',
          field: 'teamRole',
          sortable: true,
          index: 3,
        },
        {
          column: 'Status',
          field: 'status',
          positon: 'center',
          sortable: true,
          index: 4,
        },
        {
          column: 'Response Ratio',
          columnTooltip:
            'The total number of responses provided over the total number of Team Lead Instructions',
          field: 'responseRatio.replies',
          icon: 'comment',
          model: row => `${row.responseRatio.replies} / ${row.responseRatio.total}`,
          positon: 'center',
          sortable: true,
          unChecked: true,
          index: 5,
        },
        {
          column: 'Selection Ratio',
          columnTooltip:
            'The total number of responses or voluntary annotations Team Lead has accepted over the total number possible',
          field: 'selectionRatio.replies',
          icon: 'comment',
          model: row => `${row.selectionRatio.replies} / ${row.selectionRatio.total}`,
          positon: 'center',
          sortable: true,
          unChecked: true,
          index: 6,
        },
        {
          column: 'Last Annotation',
          field: 'user.name',
          model: row => {
            if (
              !this.counterpointData?.counterpointsFlat ||
              this.counterpointData.counterpointsFlat.length === 0
            ) {
              return '-';
            }
            let ann = this.counterpointData.counterpointsFlat.reduce(
              (prevAnn, currAnn) => {
                if (currAnn.author._id === row.user._id) {
                  if (+new Date(currAnn.updatedAt) > +new Date(prevAnn.updatedAt)) {
                    return currAnn;
                  }
                }
                return prevAnn;
              },
              { updatedAt: new Date(0) }
            );
            if (ann?.updatedAt && +new Date(ann.updatedAt) > 0) {
              let date = new Date(ann.updatedAt);
              return `${(date.getMonth() + 1).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })}/${date.getDate()}, ${date.toTimeString().split(' ')[0]}`;
            } else {
              return '-';
            }
          },
          sortable: true,
          index: 7,
        },
        {
          column: 'Date Joined',
          field: 'addedToTeam',
          type: 'date',
          sortable: true,
          index: 8,
        },
      ],

      tableOptions: {
        tableName: 'tableDocumentTeam',
        deleteSelected: {
          action: async ({ items, success, failed }) => {
            try {
              const result = await xhr.removeTeamMembers(
                this.teamID,
                items.map(item => item._id),
                this.idToken
              );
              // this.addSessionLog({
              //   location: 'DocumentTeam.vue: deleteSelected: removeTeamMembers',
              //   initialStance: this.documentData.initialStance,
              //   sourceType: this.documentData.documentType,
              //   currentTeam: result
              // })
              this.setCurrentTeam(result);
              this.setTeams(result);
              this.$refs.dataTable.loadData();
              success();
            } catch (err) {
              failed();
            }
          },
          title: 'Removing Member from Team',
        },
      },

      selectFields: 'name,activeDocuments,lastDocument',
      selectMembersList: [],
      savingStatus: '',
    };
  },

  computed: {
    LoadingStatus() {
      return LoadingStatus;
    },
    UIkit() {
      return UIkit;
    },
    EventBus() {
      return EventBus;
    },

    ...mapState([
      'userData',
      'documentData',
      'counterpointData',
      'documentID',
      'idToken',
      'currentTeam',
      'generalInstructions',
    ]),

    ...mapGetters(['teamMembers', 'sortAndFilter', 'tableToggleData']),

    teamMemberFormButtons() {
      return [this.teamMemberFormButtonsDef.Close, this.teamMemberFormButtonsDef.Primary];
    },
    defaultSubject() {
      if (this.dataInvitee && this.dataInvitee[1] === this.documentData._id) {
        return this.dataInvitee[0].subject;
      } else {
        if (this.documentData.initialStance === 'Host')
          return 'Can you find the flaws in these ideas?';
        else return 'Can you find the flaws in these arguments?';
      }
    },
    defaultGreetingMessage() {
      return `Hi ${this.teamMemberInvitation.invitees.map(member => member.firstname).join(', ')},`;
    },
    defaultCustomMessage() {
      if (this.dataInvitee && this.dataInvitee[1] === this.documentData._id) {
        return this.dataInvitee[0].customMessage;
      } else {
        return `${this.userData.first_name} ${this.userData.last_name} from ${this.userData.my_affiliation ? this.userData.my_affiliation : ''} would love to have your best ${this.documentData.initialStance === 'Host' ? 'critique of' : 'defense of'} ${this.documentData.articleTitle ? '"' + this.documentData.articleTitle + '"' : ''} in the debate ${this.documentData?.title}.`;
      }
    },
    defaultFixedMessage() {
      return `If you choose to accept the invitation, you can help to bring out the best ideas from ${this.userData.display_name}'s side.`;
    },

    teamID() {
      return this.team._id;
    },

    team() {
      return this.currentTeam;
    },
  },

  watch: {
    currentTeam() {
      console.log('WATCH currentTeam');
      this.teamLoaded++;
    },

    dateTimeInputEnabled(value) {
      if (this.generalInstructions?.timeframes?.length > 0) {
        this.generalInstructions.timeframes.at(-1).enabled = value;
      }
    },

    currentStep(value) {
      if (value === this.totalSteps) {
        this.dataInvitee = JSON.parse(localStorage.getItem('InviteeTeam')) || {};
        this.teamMemberInvitation.greetingMessage = this.defaultGreetingMessage;
        this.teamMemberInvitation.customMessage = this.defaultCustomMessage;
        this.teamMemberInvitation.fixedMessage = this.defaultFixedMessage;
        this.teamMemberInvitation.subject = this.defaultSubject;
      }
    },
  },

  async created() {
    window.addEventListener('resize', this.myEventHandler);

    // await this.loadGeneralInstructions();
    if (this.generalInstructions?.timeframes?.length > 0) {
      this.dateTimeInputEnabled = this.generalInstructions.timeframes.at(-1).enabled;
    }
  },
  mounted() {
    this.dataInvitee = JSON.parse(localStorage.getItem('InviteeTeam')) || {};
    if (this.documentData?._id && this.documentData._id === this.dataInvitee[1]) {
      this.isDataSaved = true;
      this.teamMemberInvitation = this.dataInvitee[0];
    }
    EventBus.on('import.contacts.show', this.contactsValue);
    EventBus.on('import.team.show', this.teamValue);
    EventBus.on('hide.modals.team', this.closeNewMemberModal);
    this.myEventHandler();

    UIkit.util.on(this.$refs.newMemberFormModal, 'hide', async () => {
      if (!this.isDataSaved) {
        ((this.teamMemberInvitation = {
          invitees: [
            {
              firstname: '',
              email: '',
              lastname: '',
              phone: '',
              order: 1,
            },
          ],
          greetingMessage: '',
          customMessage: '',
          fixedMessage: '',
          rulesMessage: 'Clearly state any rules for your team.',
          teamName: '',
          timeThreshold: 24,
        }),
          (this.currentStep = 1));
        this.maxStepVisited = 1;
      }
    });
  },
  beforeUnmount() {
    EventBus.off('import.contacts.show', this.contactsValue);
    EventBus.off('import.team.show', this.teamValue);
    EventBus.off('hide.modals.team', this.closeNewMemberModal);
  },
  unmounted() {
    window.removeEventListener('resize', this.myEventHandler);
  },
  methods: {
    ...mapMutations(['setUserData', 'setCurrentTeam', 'setTeams', 'hideSidebar', 'showSidebar']),

    ...mapActions([
      'loadNonMembersData',
      'removeMultipleTeamMembers',
      // 'loadGeneralInstructions',
      'saveGeneralInstructions',
      'addSessionLog',
    ]),
    myEventHandler() {
      if (window.innerWidth < 1500) {
        this.hideSidebar();
      } else if (window.innerWidth > 1500) {
        this.showSidebar();
      }
    },
    contactsValue(value) {
      this.contactView = value;
    },
    teamValue(value) {
      this.teamView = value;
    },
    async getTeamMembers({ sortColumn, sortDirection, filters }) {
      let data = this.team?.members?.filter(tm => tm.active) || [];
      data = this.sortAndFilter({ data, sortColumn, sortDirection, filters });
      return { results: data };
    },

    getCounterpointValue(field) {
      return this.documentData?.counterpoint?.counterpoint[0]
        ? this.documentData.counterpoint[0][field]
        : 0;
    },

    async openSelectMembersModal() {
      UIkit.modal(this.$refs['selectMembers']).show();
      this.selectMembersList = await this.loadNonMembersData();
    },

    closeSelectMembersModal() {
      UIkit.modal(this.$refs['selectMembers']).hide();
    },

    async handleStep(step) {
      if (this.currentStep === 1) {
        this.setInviteesMembers(step);
        return;
      }
      if (this.currentStep === 2 && this.generalInstructions?.timeframes?.length > 0) {
        if (this.generalInstructions.timeframes.at(-1).enabled) {
          if (!this.generalInstructions.timeframes.at(-1).timeframeThreshold) {
            EventBus.emit('generalinstruction.dateerror', '');
            return;
          }
        }
      }
      this.currentStep = step;
      if (this.currentStep > this.maxStepVisited) {
        this.maxStepVisited = this.currentStep;
      }
    },

    setInviteesMembers(step) {
      EventBus.emit('invitation.teamLead.invitees.new', {
        success: formData => {
          this.teamMemberInvitation.invitees = formData.invitees;
          this.teamMemberInvitation.teamName = formData.teamName;
          this.currentStep = step;
          if (this.currentStep > this.maxStepVisited) {
            this.maxStepVisited = this.currentStep;
          }
        },
      });
    },

    handleSave() {
      this.isDataSaved = true;
      let dataInvitee = JSON.parse(localStorage.getItem('InviteeTeam')) || {};
      localStorage.setItem(
        'InviteeTeam',
        JSON.stringify([this.teamMemberInvitation, this.documentData._id])
      );
      console.log('dataInviteeTeam', dataInvitee);
      this.closeNewMemberModal();
    },

    openNewMemberModal() {
      this.savingStatus = LoadingStatus.NONE;
      UIkit.modal(this.$refs['newMemberFormModal']).show();
    },

    closeNewMemberModal() {
      UIkit.modal(this.$refs['newMemberFormModal']).hide();
    },

    clickMainButton() {
      this.openNewMemberModal();
    },

    handleSuccess() {
      this.closeNewMemberModal();
      this.$emit('memberAdded');
    },

    loadTeamMembersData() {
      xhr
        .getUsersData(
          this.teamMembers
            .map(tm => (tm.user ? (tm.user._id ? tm.user._id : tm.user) : tm))
            .join(','),
          this.selectFields,
          this.idToken
        )
        .then(res => {
          this.documentData.team.members = this.documentData.team.members.map(tm => {
            return {
              ...tm,
              ...res.find(r => r._id == tm.user),
            };
          });
        });
    },

    handleAddToTeam() {
      this.loadTeamMembersData();
      this.closeSelectMembersModal();
    },

    handleCreateTeamMember() {
      const member = this.$refs.teamMemberForm.submitForm();
      if (member) this.openInvitationModal(member);
    },

    openInvitationModal(member) {
      this.teamMemberInvitation.invitees = [member];
      UIkit.modal(this.$refs['invitationConfirm']).show();
    },

    closeInvitationModal() {
      UIkit.modal(this.$refs['invitationConfirm']).hide();
      this.savingStatus = LoadingStatus.NONE;
    },

    async sendTeamMemberInvitation() {
      this.disabelBtn = true;
      this.savingStatus = LoadingStatus.LOADING;
      const invitees = this.teamMemberInvitation.invitees;
      const customMessage = this.teamMemberInvitation.customMessage;
      const greetingMessage = this.teamMemberInvitation.greetingMessage;
      const fixedMessage = this.teamMemberInvitation.fixedMessage
        .replace("<span class='acc-dec-link'> { Accept }</span>", '{ Accept }')
        .replace("<span class='acc-dec-link'> { Decline }</span>", '{ Decline }')
        .replaceAll('\n', '');
      const subject = this.teamMemberInvitation.subject;
      const teamName = this.teamMemberInvitation.teamName;
      const timeThreshold =
        this.generalInstructions?.timeframes?.length > 0
          ? parseFloat(this.generalInstructions.timeframes.at(-1).timeframeThreshold)
          : 24;
      const createTeamMembersObj = {
        invitees: invitees.map((invitee, index) => ({
          email: invitee.email,
          firstname: invitee.firstname,
          lastname: invitee.lastname,
          phone: invitee.phone,
          order: index + 1,
        })),
        greetingMessage,
        customMessage,
        fixedMessage,
        subject,
        timeThreshold,
        teamName,
        instructions: this.generalInstructions?.instructions || '',
        timeframeThreshold:
          this.generalInstructions?.timeframes?.length > 0
            ? parseFloat(this.generalInstructions.timeframes.at(-1).timeframeThreshold)
            : undefined,
        timeframeEnabled:
          this.generalInstructions?.timeframes?.length > 0
            ? this.generalInstructions.timeframes.at(-1).enabled
            : false,
      };

      try {
        if (this.documentData?.discussionType === 'curate') {
          await xhr.inviteCohortTeamLead(this.teamID, createTeamMembersObj, this.idToken);
        } else {
          await xhr.inviteTeamMember(this.teamID, createTeamMembersObj, this.idToken);
        }
        let generalInstructionsData = {
          instructions: this.generalInstructions?.instructions || '',
        };
        if (
          this.generalInstructions?.timeframes?.length > 0 &&
          this.generalInstructions.timeframes.at(-1).timeframeThreshold
        ) {
          generalInstructionsData.timeframeThreshold = parseFloat(
            this.generalInstructions.timeframes.at(-1).timeframeThreshold
          );
          generalInstructionsData.timeframeEnabled =
            this.generalInstructions.timeframes.at(-1).enabled;
        }
        await this.saveGeneralInstructions({
          teamID: this.teamID,
          generalInstructions: generalInstructionsData,
        });
        // this.addSessionLog({
        //   location: 'DocumentTeam.vue: sendTeamMemberInvitation: inviteTeamMember',
        //   initialStance: this.documentData.initialStance,
        //   sourceType: this.documentData.documentType,
        //   currentTeam: res
        // })
        let resTeam = await xhr.getTeamDetails(this.teamID);
        this.setCurrentTeam(resTeam);
        this.setTeams(resTeam);
        this.savingStatus = LoadingStatus.SUCCESS;
        this.closeInvitationModal();
        this.isDataSaved = false;
        this.disabelBtn = false;
        this.currentStep = 1;
        this.maxStepVisited = 1;
        this.$refs.dataTable.loadData();
        this.closeNewMemberModal();
      } catch (err) {
        this.disabelBtn = false;
        this.savingStatus = LoadingStatus.FAILED;
        alert('Error adding new member to Team');
        console.error(err);
      }
    },

    teamMemberFormInvalid(value) {
      this.teamMemberFormButtonsDef.Primary.disabled = value;
    },
  },
};
</script>

<style lang="sass">
.custom-stroke svg > *
  stroke-width: 2px
  stroke: black !important
.message-box-width
  box-sizing: border-box
  width: 117.8%
  max-width: 100%

.bg-white
  background-color: white !important
.disable
  pointer-events: none
  opacity: 0.4
.box-margin-left
  margin-left: 35%
.newMemberFormWidth
  width: 44% !important
  margin-left: 30% !important
  @media only screen and (max-width: 1852px)
    width: 45% !important
    margin-left: 30% !important
  @media only screen and (max-width: 1670px)
    width: 50% !important
    margin-left: 27% !important
  @media only screen and (max-width: 1504px)
    width: 56% !important
    margin-left: 24% !important
  @media only screen and (max-width: 1300px)
    margin-left: 20% !important
    width: 58% !important
  @media only screen and (max-width: 1200px)
    margin-left: 20% !important
    width: 60% !important
  @media only screen and (max-width: 1132px)
    margin-left: 17% !important
    width: 70% !important

.newMemberFormImportView
  width: 44% !important
  margin-left: 17% !important
  @media only screen and (max-width: 1852px)
    width: 45% !important
    margin-left: 17% !important
  @media only screen and (max-width: 1670px)
    width: 49% !important
    margin-left: 17% !important
  @media only screen and (max-width: 1555px)
    width: 50% !important
    margin-left: 17% !important
  @media only screen and (max-width: 1500px)
    width: 53% !important
    margin-left: 9% !important
  @media only screen and (max-width: 1300px)
    margin-left: 4% !important
    width: 55% !important
  @media only screen and (max-width: 1200px)
    margin-left: 3% !important
    width: 60% !important
  @media only screen and (max-width: 1132px)
    margin-left: 4% !important
    width: 70% !important
</style>
